import { useState } from 'react'
import { LEVEL_ICONS, type Level, type LevelIcon } from '@workflow/contracts'
import { Button, Checkbox, cn, EmptyNote, Field, Icons, Loading, StateNote, TextField } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useCreateLevel, useLevels, useUpdateLevel } from '@/lib/api/hooks'
import { countOf } from '@/lib/format'
import { amountText } from '@/features/payments/sheetMath'
import { LevelGlyph } from '@/features/levels/LevelMark'

/**
 * The levels, administered: each member stands on one, Level 0 from the day
 * they join. An administrator names the levels, gives each a mark, adds new
 * ones above the highest, and decides what each carries. The only attribute
 * so far is extra money — the amount a new payment sheet starts the player's
 * extra at, once per month. The sheet's extra stays editable per player.
 */
export function LevelsPane() {
  const levels = useLevels()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Level | null>(null)
  const close = () => {
    setCreating(false)
    setEditing(null)
  }

  return (
    <div className="flex flex-col gap-6">
      {creating || editing ? (
        <LevelForm initial={editing ?? undefined} onDone={close} onCancel={close} />
      ) : (
        <div>
          <Button size="sm" variant="primary" icon={<Icons.plus />} onClick={() => setCreating(true)}>
            Add a level
          </Button>
          <p className="mt-1.5 text-caption text-muted-foreground">Players start on the lowest level; raise them from People.</p>
        </div>
      )}

      <div>
        <h3 className="text-micro text-muted-foreground uppercase">Levels</h3>
        <div className="mt-1.5">
          {levels.isLoading ? (
            <Loading label="Loading levels" />
          ) : levels.error ? (
            <StateNote title="The levels could not be loaded." detail={describeError(levels.error)} />
          ) : (levels.data ?? []).length === 0 ? (
            <EmptyNote title="No levels yet." detail="Add the first level — everyone stands on one." />
          ) : (
            <ul className="flex flex-col">
              {(levels.data ?? []).map((level) => (
                <li key={level.id} className="border-b border-hairline last:border-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 py-2.5">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-tint text-foreground">
                      <LevelGlyph icon={level.icon} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-ui font-medium text-foreground">{level.name}</span>
                      <span className="block truncate text-caption text-muted-foreground">
                        {countOf(level.members, 'player')}
                      </span>
                    </span>
                    <span className="text-caption text-muted-foreground tabular-nums" title="Extra money each month">
                      {level.attributes.extra_money === null
                        ? 'No extra money'
                        : level.attributes.extra_money === 0
                          ? 'Extra money: 0/='
                          : `Extra money: ${amountText(level.attributes.extra_money)}`}
                    </span>
                    <Button size="xs" variant="ghost" onClick={() => setEditing(level)}>
                      Edit
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

/** The creation and the mid-edit: the name, the mark, and the attributes it carries. */
function LevelForm({ initial, onDone, onCancel }: { initial?: Level; onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState(initial?.name ?? '')
  const [icon, setIcon] = useState<LevelIcon>(initial?.icon ?? 'gem')
  const startingExtra = initial ? initial.attributes.extra_money : null
  const [carriesExtra, setCarriesExtra] = useState(startingExtra !== null)
  const [extra, setExtra] = useState(startingExtra === null ? '' : String(startingExtra / 100))
  const create = useCreateLevel()
  const update = useUpdateLevel()

  const busy = create.isPending || update.isPending
  const error = create.error ?? update.error
  const extraInvalid = carriesExtra && (extra.trim() === '' || !Number.isFinite(Number(extra)) || Number(extra) < 0)

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const input = {
      name: name.trim(),
      icon,
      attributes: { extra_money: carriesExtra ? Math.round(Number(extra) * 100) : null },
    }
    if (initial) update.mutate({ id: initial.id, input }, { onSuccess: onDone })
    else create.mutate(input, { onSuccess: onDone })
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border bg-surface px-5 py-4 shadow-chip">
      <Field label="Name" htmlFor="level-name" hint="What people see on their profile.">
        <TextField id="level-name" value={name} autoFocus placeholder="e.g. Level 6" onChange={(event) => setName(event.target.value)} />
      </Field>
      <Field label="Mark" hint="The icon beside the name.">
        <div role="radiogroup" aria-label="Mark" className="flex flex-wrap gap-1.5">
          {LEVEL_ICONS.map((option) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={icon === option}
              aria-label={option}
              onClick={() => setIcon(option)}
              className={cn(
                'flex size-9 items-center justify-center rounded-lg border outline-none transition-colors duration-fast focus-visible:ring-1 focus-visible:ring-foreground',
                icon === option ? 'border-foreground bg-tint text-foreground' : 'border-hairline text-muted-foreground hover:bg-tint hover:text-foreground',
              )}
            >
              <LevelGlyph icon={option} />
            </button>
          ))}
        </div>
      </Field>

      <h4 className="mt-4 text-micro text-muted-foreground uppercase">What this level carries</h4>
      <div className="mt-2 flex flex-col gap-2">
        <Checkbox checked={carriesExtra} onChange={setCarriesExtra}>
          Extra money
        </Checkbox>
        {carriesExtra ? (
          <Field
            label="Amount each month"
            htmlFor="level-extra"
            hint="BDT. A new payment sheet starts this player's extra at this amount, once per month however many jobs they worked. You can still change it on the sheet."
            error={extraInvalid ? 'The amount is a number, 0 or more.' : undefined}
          >
            <TextField id="level-extra" value={extra} inputMode="decimal" placeholder="e.g. 3000" onChange={(event) => setExtra(event.target.value)} />
          </Field>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" variant="primary" busy={busy} disabled={!name.trim() || extraInvalid}>
          {initial ? 'Save level' : 'Add level'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        {error ? <p className="text-caption text-danger">{describeError(error)}</p> : null}
      </div>
    </form>
  )
}
